import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import useIsMobile from '../../hooks/useIsMobile';

const ZecherScore = ({ score }) => {
  const isMobile = useIsMobile();
  
  const getScoreColor = (score) => {
    if (score >= 80) return '#059669'; // green
    if (score >= 60) return '#2563EB'; // blue
    if (score >= 40) return '#D97706'; // yellow/orange
    return '#DC2626'; // red
  };

  const getScoreMessage = (score) => {
    if (score >= 80) return "Excellent! Keep up the great work!";
    if (score >= 60) return "Good job! You're on the right track.";
    if (score >= 40) return "Room for improvement.";
    return "Let's work on improving your score.";
  };

  const scoreColor = getScoreColor(score);
  const circleSize = isMobile ? 120 : 128;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Zecher Score</Text>
      
      <View style={styles.content}>
        <View style={[styles.scoreCircleContainer, isMobile && styles.scoreCircleContainerMobile]}>
          <View style={[styles.circleWrapper, { width: circleSize, height: circleSize }]}>
            {/* Background Circle */}
            <View 
              style={[
                styles.backgroundCircle,
                { 
                  width: circleSize, 
                  height: circleSize,
                  borderRadius: circleSize / 2,
                }
              ]} 
            />
            
            {/* Progress Circle using conic gradient simulation */}
            <View 
              style={[
                styles.progressRing,
                { 
                  width: circleSize, 
                  height: circleSize,
                  borderRadius: circleSize / 2,
                }
              ]} 
            >
              {/* Score percentage bars */}
              {Array.from({ length: 20 }, (_, i) => {
                const angle = (i * 18) - 90; // 20 segments, 18 degrees each, start at top
                const isActive = i < Math.floor((score / 100) * 20);
                return (
                  <View
                    key={i}
                    style={[
                      styles.progressSegment,
                      {
                        backgroundColor: isActive ? scoreColor : '#E5E7EB',
                        transform: [{ rotate: `${angle}deg` }],
                        width: circleSize * 0.08,
                        height: circleSize * 0.25,
                        top: circleSize * 0.02,
                        left: circleSize * 0.46,
                      }
                    ]}
                  />
                );
              })}
            </View>
            
            {/* Inner white circle to create ring effect */}
            <View 
              style={[
                styles.innerCircle,
                { 
                  width: circleSize * 0.75, 
                  height: circleSize * 0.75,
                  borderRadius: (circleSize * 0.75) / 2,
                }
              ]} 
            />
            
            {/* Score Text */}
            <View style={styles.scoreTextContainer}>
              <Text style={[styles.scoreText, { color: scoreColor }, isMobile && styles.scoreTextMobile]}>
                {score}
              </Text>
            </View>
          </View>
        </View>
        
        <Text style={[styles.message, isMobile && styles.messageMobile]}>
          {getScoreMessage(score)}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 16,
  },
  content: {
    alignItems: 'center',
  },
  scoreCircleContainer: {
    marginBottom: 16,
  },
  scoreCircleContainerMobile: {
    marginBottom: 12,
  },
  circleWrapper: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backgroundCircle: {
    position: 'absolute',
    backgroundColor: '#F3F4F6',
  },
  progressRing: {
    position: 'absolute',
    overflow: 'hidden',
  },
  progressSegment: {
    position: 'absolute',
    borderRadius: 2,
    transformOrigin: 'center bottom',
  },
  innerCircle: {
    position: 'absolute',
    backgroundColor: '#FFFFFF',
    zIndex: 5,
  },
  scoreTextContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  scoreText: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  scoreTextMobile: {
    fontSize: 32,
  },
  message: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 20,
  },
  messageMobile: {
    fontSize: 16,
    lineHeight: 22,
    maxWidth: 250,
  },
});

export default ZecherScore; 